import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FuelGridItem,
  FuelIcon,
  FuelInteractiveIcon,
  FuelSkeleton,
  FuelTypography,
} from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import { useTimeout } from '@hooks/useTimeout';
import useAmplitude from '@hooks/useAmplitude';
import { formatDateToShortString } from '@utils/formatters';
import AppContext from '@context';
import { JourneysStateInterface } from '@context/reducers/journeys/journeys.type';
import BaseModal from '@components/ui/BaseModal';
import TruckerInfo from '@components/ui/Trucker/TruckerInfoV2';
import {
  JourneyCreator,
  JourneyGetResponseMonitoringInterface,
} from '@interface/journeys';
import useLoadJourney from '@hooks/useLoadJourney';
import LastLocation from '@components/ui/Location/LastLocationV2';
import { JOURNEY_STATUS_DONE } from '@constants';
import MonitoringMap from '@components/ui/MonitoringMap/MonitoringMapContainer';
import { ModalMonitoringPropsInterface } from './ModalMonitoringTypes';
import Action from './Action';
import Disclaimers from './Disclaimers/Disclaimers';
import Details from './Details';
import History from './History';
import OriginAndDestination from './OriginAndDestination';
import * as Styled from './ModalMonitoringStyles';

function ModalMonitoring({
  isMobile,
  handlerOpenModalConfirm,
}: ModalMonitoringPropsInterface) {
  const {
    state: { ui, journeys },
    dispatch,
  } = useContext(AppContext);
  const { isShow, flow, journeyId } = ui.modalMonitoring;
  const baseRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);
  const mapContentRef = useRef<HTMLDivElement | null>(null);
  const [widthMapContent, setWidthMapContent] = useState(0);

  const {
    data: journeySelection,
    isFetching,
    isError,
  } = journeys.selection as JourneysStateInterface['selection'];

  const origin = journeySelection?.journey?.address?.pickup.description;
  const destination = journeySelection?.journey?.address?.delivery.description;
  const journey = journeySelection?.journey;
  const page = journeySelection?.page;
  const addTimeout = useTimeout();
  const { amplitudeEvent } = useAmplitude();
  const { fetchJourney } = useLoadJourney({
    journeyId: journeyId || '',
  });
  const navigate = useNavigate();

  function handleSubtitle(createdBy?: JourneyCreator) {
    const subtitle: { [key in JourneyCreator]: string } = {
      trucker: 'Criada pelo motorista em',
      trucker_indication:
        'Viagem criada a partir de identificação de pagamento pelo caminhoneiro em',
      company: 'Viagem criada manualmente pela empresa em',
      payment_identifier:
        'Viagem criada automaticamente a partir de pagamento em',
      'trucker:deposit:fee':
        'Viagem criada a partir da tarifa reembolsável Fretebras em',
      'gr:fretebras':
        'Viagem criada automaticamente a partir da GR aprovada em',
      'company:nca:manually': 'Viagem criada manualmente pela empresa em',
      'tms:fretebras': 'Viagem criada automaticamente pelo TMS Fretebras em',
      'company:banking:credit':
        'Viagem criada a partir de pagamento de crédito em',
      'chat:negotiation:automatic':
        'Viagem criada a partir de negociação no chat em',
    };
    if (createdBy && subtitle[createdBy]) {
      return subtitle[createdBy];
    }

    return 'Criada em';
  }

  function onClose() {
    return new Promise<void>((resolve) => {
      baseRef.current?.classList.remove('animated');
      contentRef.current?.classList.remove('animated');
      actionRef.current?.classList.remove('animated');

      addTimeout(() => {
        dispatch({
          type: 'ui_modalMonitoring_set_show',
          payload: { isShow: false, journeyId: '' },
        });
        resolve();
      }, 200);
    });
  }

  function handleClose() {
    onClose().then(() => {
      navigate('/viagens');
      dispatch({
        type: 'journeys_selection_clear',
        payload: {
          isFetching: false,
          isError,
          data: null,
        },
      });
    });
  }

  const handleOpenTruckerProfile = () => {
    if (journey?.trucker?.encodedPersonId) {
      amplitudeEvent(
        'journey_monitoring_open_trucker_profile',
        amplitudeCommonData(journey),
      );
      navigate(`/perfil-motorista/${journey?.trucker?.encodedPersonId}`);
    }
  };

  function amplitudeCommonData(
    journeyData?: JourneyGetResponseMonitoringInterface['data']['journey'],
  ) {
    return {
      journey_id: journeyData?.id || '',
    };
  }

  function handleTilte() {
    return (
      <FuelFlex
        defaultProps={{
          justifyContent: 'space-between',
          gap: 'km6',
        }}
      >
        {isFetching && (
          <FuelSkeleton
            shape="square"
            size="custom"
            width="346px"
            height="20px"
          />
        )}
        {!isFetching && journey && (
          <Styled.ModalTitle data-testid="modalMonitoringHedader">
            <FuelTypography
              size="body-md"
              weight="bold"
              color="neutral-strong"
              spacingRight={8}
            >
              {handleSubtitle(journey.created.by)}
              {` `}
              {formatDateToShortString(journey.created.at)?.toUpperCase()}
            </FuelTypography>
            <FuelIcon name="information" iconStyle="filled" size="micro" />
          </Styled.ModalTitle>
        )}
        <FuelInteractiveIcon
          data-testid="button-close"
          icon="close"
          size="medium"
          onClick={() => {
            if (journey) {
              amplitudeEvent('journey_monitoring_closed', {
                source: 'x-fechar-acompanhamento-viagem',
                ...amplitudeCommonData(journey),
              });
            }
            handleClose();
          }}
        />
      </FuelFlex>
    );
  }

  function handleTruckerInfo() {
    if (isFetching) {
      return (
        <Styled.Section>
          <FuelFlex defaultProps={{ alignItems: 'flex-start', gap: 'km3' }}>
            <FuelSkeleton
              shape="circle"
              size="custom"
              width="40px"
              height="40px"
            />
            <Styled.ContentSkeleton>
              <FuelSkeleton
                shape="square"
                size="custom"
                width="108px"
                height="18px"
              />
              <FuelSkeleton
                shape="square"
                size="custom"
                width="108px"
                height="20px"
              />
            </Styled.ContentSkeleton>
            <FuelFlex defaultProps={{ gap: 'km2', customMargin: '0 0 0 auto' }}>
              <FuelSkeleton
                shape="square"
                size="custom"
                width="32px"
                height="32px"
              />
              <FuelSkeleton
                shape="square"
                size="custom"
                width="154px"
                height="32px"
              />
            </FuelFlex>
          </FuelFlex>
        </Styled.Section>
      );
    }
    if (!journey?.trucker?.name) {
      return null;
    }
    return (
      <Styled.Section>
        <TruckerInfo
          avatar={journey?.trucker.avatar}
          name={journey?.trucker?.name || ''}
          phone={
            journey?.trucker?.phone
              ? { number: journey?.trucker?.phone, showButton: true }
              : undefined
          }
          onCallbackCopyPhone={(phone) => {
            amplitudeEvent('journey-monitoring-telephone-copied', {
              source: 'copiar-telefone-monitorar-viagem',
              journey_id: journey?.id || '',
              trucker_id: journey?.trucker.id || '',
              numero_copiado: phone,
            });
          }}
          onCallbackSentWhatsApp={() => {
            amplitudeEvent('journey_whatsapp_for_trucker_sent', {
              source: 'monitorar-viagem',
              journey_id: journey?.id || '',
              trucker_id: journey?.trucker.id || '',
            });
          }}
          onCallbackClickProfile={handleOpenTruckerProfile}
          depositFee={page?.depositFee}
          verified={journey?.trucker?.verified}
          vip={journey?.trucker?.vip}
          journeyCount={journey?.trucker?.journeyCount}
          rating={journey?.trucker?.rating}
        />
      </Styled.Section>
    );
  }

  function handleTruckerDepositFee() {
    if (isFetching) {
      return (
        <Styled.Section>
          <FuelSkeleton
            shape="square"
            size="custom"
            width={!isMobile ? '462px' : '360px'}
            height="52px"
          />
        </Styled.Section>
      );
    }
    return <Disclaimers />;
  }

  function renderMonitoringMap() {
    return isFetching ? (
      <Styled.Section>
        <FuelFlex>
          <FuelSkeleton
            shape="square"
            size="custom"
            width="536px"
            height="288px"
          />
        </FuelFlex>
      </Styled.Section>
    ) : (
      <FuelGridItem sm={2} md={2} lg={4} xlg={4}>
        <div
          ref={(element) => {
            mapContentRef.current = element;

            addTimeout(() => {
              if (mapContentRef.current) {
                setWidthMapContent(
                  mapContentRef.current.getBoundingClientRect().width,
                );
              }
            }, 500);
          }}
        >
          <MonitoringMap data={journeySelection} width={widthMapContent} />
        </div>
      </FuelGridItem>
    );
  }

  useEffect(() => {
    if (isShow) {
      addTimeout(() => {
        baseRef.current?.classList.add('animated');
        contentRef.current?.classList.add('animated');
        actionRef.current?.classList.add('animated');
      }, 400);
      fetchJourney();
    }
  }, [isShow]);

  useEffect(() => {
    if (isShow && !isFetching && !isError) {
      amplitudeEvent('journey_monitoring_viewed', {
        source: flow === 'button' ? 'acompanhamento-viagem' : 'lista-viagem',
        journey_id: journey?.id || '',
        trucker_id: journey?.trucker.id || '',
        journey_status: journey?.status.label || '',
        pickup: journey?.address.pickup.description || '',
        delivery: journey?.address.delivery.description || '',
        has_current_location:
          !!journey?.trucker.lastLocation?.cityName &&
          !!journey?.trucker.lastLocation?.stateAcronym
            ? 'sim'
            : 'nao',
      });
    }
    if (!isFetching && isError) {
      amplitudeEvent('journey_monitoring_viewed_error', {
        source: 'visualizar-drawer-error',
        journey_id: journeyId || '',
      });
      handleClose();
    }
  }, [isFetching, isError]);

  function getOccurrence() {
    if (!journey?.occurrences || !journey.occurrences.length) {
      return undefined;
    }

    return journey?.occurrences[0];
  }

  const currentOccurrence = getOccurrence();

  return (
    <BaseModal
      isOpen={isShow}
      onClose={() => {
        amplitudeEvent('journey_monitoring_closed', {
          source: 'area-cinza-acompanhar-viagem',
          ...amplitudeCommonData(journey),
        });

        handleClose();
      }}
      innerRef={baseRef}
      vAlignment="top"
      hAlignment="right"
      hOverlay
    >
      <Styled.WrapperContentModal isShow={isShow} ref={contentRef}>
        {handleTilte()}
        {handleTruckerInfo()}
        {handleTruckerDepositFee()}
        <Styled.Section gap="km8">
          <LastLocation
            isDone={JOURNEY_STATUS_DONE.includes(journey?.status?.slug || '')}
            changedStatusAt={journey?.status.details.updatedAt || ''}
            isLoading={journeys?.selection?.isFetching}
            location={{
              cityName: journey?.trucker.lastLocation.cityName || '',
              stateAcronym: journey?.trucker.lastLocation.stateAcronym || '',
              updatedAt: journey?.trucker.lastLocation.timestamp || '',
            }}
            occurrence={currentOccurrence}
            onClickReload={() => {
              if (
                journey?.status.slug !== 'done' &&
                journey?.status.slug !== 'unrecognized'
              ) {
                fetchJourney();
                amplitudeEvent('journey-monitoring-refresh-clicked', {
                  source: 'recarregar-acompanhamento-viagem',
                  journey_id: journey?.id || '',
                  trucker_id: journey?.trucker.id || '',
                  journey_status: journey?.status.slug || '',
                  pickup_state: origin || '',
                  delivery_state: destination || '',
                });
              }
            }}
          />
        </Styled.Section>
        {renderMonitoringMap()}
        <History />
        <OriginAndDestination />
        <Details details={journey?.details} />
        <Action
          handlerOpenModalConfirm={handlerOpenModalConfirm}
          innerRef={actionRef}
        />
      </Styled.WrapperContentModal>
    </BaseModal>
  );
}

export default ModalMonitoring;
